// Non-component exports for VoiceProfilePanel — split out so the .tsx file is
// components-only (satisfies react-refresh/only-export-components).

export type SynthesisRow = {
  referenced_concept: string;
  usage_pattern: string;
  key_terms: string;
};

export type RhetoricalMoveRow = {
  move: string;
  description: string;
  example_pattern: string;
};

export type HighFrequencyTermRow = {
  category: string;
  terms: string;
};

export type SignaturePhraseRow = {
  phrase: string;
  how_used: string;
};

export type VerbalMarkerRow = {
  marker: string;
  how_used: string;
};

export type SentenceArchitectureRow = {
  pattern: string;
  application: string;
};

export type ParagraphFlowRow = {
  section: string;
  application_flow: string;
};

export type BuildingBlockProtocolRow = {
  protocol: string;
  directive: string;
};

export type RedLineRow = {
  value: string;
};

export type AvoidTermRow = {
  value: string;
};

export type MisrepresentationRow = {
  value: string;
};

export type AuthenticityMarkerRow = {
  value: string;
};

export type PeakExemplarRow = {
  value: string;
};


export const EMPTY_SYNTHESIS_ROW: SynthesisRow = {
  referenced_concept: "",
  usage_pattern: "",
  key_terms: "",
};

export const EMPTY_RHETORICAL_ROW: RhetoricalMoveRow = {
  move: "",
  description: "",
  example_pattern: "",
};

export const EMPTY_HIGH_FREQUENCY_TERM_ROW: HighFrequencyTermRow = {
  category: "",
  terms: "",
};

export const EMPTY_SIGNATURE_PHRASE_ROW: SignaturePhraseRow = {
  phrase: "",
  how_used: "",
};

export const EMPTY_VERBAL_MARKER_ROW: VerbalMarkerRow = {
  marker: "",
  how_used: "",
};

export const EMPTY_SENTENCE_ARCHITECTURE_ROW: SentenceArchitectureRow = {
  pattern: "",
  application: "",
};

export const EMPTY_PARAGRAPH_FLOW_ROW: ParagraphFlowRow = {
  section: "",
  application_flow: "",
};

export const EMPTY_BUILDING_BLOCK_PROTOCOL_ROW: BuildingBlockProtocolRow = {
  protocol: "",
  directive: "",
};

export const EMPTY_RED_LINE_ROW: RedLineRow = { value: "" };
export const EMPTY_AVOID_TERM_ROW: AvoidTermRow = { value: "" };
export const EMPTY_MISREPRESENTATION_ROW: MisrepresentationRow = { value: "" };
export const EMPTY_AUTHENTICITY_MARKER_ROW: AuthenticityMarkerRow = { value: "" };
export const EMPTY_PEAK_EXEMPLAR_ROW: PeakExemplarRow = { value: "" };




export const DEFAULT_ROW_COUNT = 3;

const makeRows = <R>(empty: R, n: number): R[] =>
  Array.from({ length: n }, () => ({ ...empty }));

export const makeEmptySynthesisRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_SYNTHESIS_ROW, n);
export const makeEmptyRhetoricalRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_RHETORICAL_ROW, n);
export const makeEmptyHighFrequencyTermRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_HIGH_FREQUENCY_TERM_ROW, n);
export const makeEmptySignaturePhraseRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_SIGNATURE_PHRASE_ROW, n);
export const makeEmptyVerbalMarkerRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_VERBAL_MARKER_ROW, n);
export const makeEmptySentenceArchitectureRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_SENTENCE_ARCHITECTURE_ROW, n);
export const makeEmptyParagraphFlowRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_PARAGRAPH_FLOW_ROW, n);
export const makeEmptyBuildingBlockProtocolRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_BUILDING_BLOCK_PROTOCOL_ROW, n);
export const makeEmptyRedLineRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_RED_LINE_ROW, n);
export const makeEmptyAvoidTermRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_AVOID_TERM_ROW, n);
export const makeEmptyMisrepresentationRows = (n = 1) => makeRows(EMPTY_MISREPRESENTATION_ROW, n);
export const makeEmptyAuthenticityMarkerRows = (n = 2) => makeRows(EMPTY_AUTHENTICITY_MARKER_ROW, n);
export const makeEmptyPeakExemplarRows = (n = DEFAULT_ROW_COUNT) => makeRows(EMPTY_PEAK_EXEMPLAR_ROW, n);

// Generic parse: JSON array of row objects with string fields, else legacy
// plain text placed into the fallback column of the first row.
function parseRowsGeneric<R extends Record<string, string>>(
  raw: string | null | undefined,
  empty: R,
  legacyKey: keyof R,
  minRows: number = DEFAULT_ROW_COUNT,
): R[] {
  const keys = Object.keys(empty) as (keyof R)[];
  const pad = (rows: R[]) => {
    while (rows.length < minRows) rows.push({ ...empty });
    return rows;
  };
  if (!raw || !raw.trim()) return pad([]);
  const trimmed = raw.trim();
  const looksLikeJson = trimmed.startsWith("[") || trimmed.startsWith("{");
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      const rows = parsed.map((r) => {
        const row = { ...empty };
        for (const k of keys) {
          const v = (r as Record<string, unknown>)?.[k as string];
          if (typeof v === "string") (row as Record<string, string>)[k as string] = v;
        }
        return row;
      });
      return pad(rows);
    }
  } catch {
    if (looksLikeJson) return pad([]);
  }
  if (looksLikeJson) return pad([]);
  const first = { ...empty };
  (first as Record<string, string>)[legacyKey as string] = raw;
  return pad([first]);
}

export const parseSynthesisRows = (raw: string | null | undefined) =>
  parseRowsGeneric<SynthesisRow>(raw, EMPTY_SYNTHESIS_ROW, "usage_pattern");

export const parseRhetoricalRows = (raw: string | null | undefined) =>
  parseRowsGeneric<RhetoricalMoveRow>(raw, EMPTY_RHETORICAL_ROW, "description");

export const parseHighFrequencyTermRows = (raw: string | null | undefined) =>
  parseRowsGeneric<HighFrequencyTermRow>(raw, EMPTY_HIGH_FREQUENCY_TERM_ROW, "terms");

export const parseSignaturePhraseRows = (raw: string | null | undefined) =>
  parseRowsGeneric<SignaturePhraseRow>(raw, EMPTY_SIGNATURE_PHRASE_ROW, "how_used");

export const parseVerbalMarkerRows = (raw: string | null | undefined) =>
  parseRowsGeneric<VerbalMarkerRow>(raw, EMPTY_VERBAL_MARKER_ROW, "how_used");

export const parseSentenceArchitectureRows = (raw: string | null | undefined) =>
  parseRowsGeneric<SentenceArchitectureRow>(raw, EMPTY_SENTENCE_ARCHITECTURE_ROW, "application");

export const parseParagraphFlowRows = (raw: string | null | undefined) =>
  parseRowsGeneric<ParagraphFlowRow>(raw, EMPTY_PARAGRAPH_FLOW_ROW, "application_flow");

export const parseBuildingBlockProtocolRows = (raw: string | null | undefined) =>
  parseRowsGeneric<BuildingBlockProtocolRow>(raw, EMPTY_BUILDING_BLOCK_PROTOCOL_ROW, "directive");

export const parseRedLineRows = (raw: string | null | undefined) =>
  parseRowsGeneric<RedLineRow>(raw, EMPTY_RED_LINE_ROW, "value");

export const parseAvoidTermRows = (raw: string | null | undefined) =>
  parseRowsGeneric<AvoidTermRow>(raw, EMPTY_AVOID_TERM_ROW, "value");

export const parseMisrepresentationRows = (raw: string | null | undefined) =>
  parseRowsGeneric<MisrepresentationRow>(raw, EMPTY_MISREPRESENTATION_ROW, "value", 1);

export const parseAuthenticityMarkerRows = (raw: string | null | undefined) =>
  parseRowsGeneric<AuthenticityMarkerRow>(raw, EMPTY_AUTHENTICITY_MARKER_ROW, "value", 2);

export const parsePeakExemplarRows = (raw: string | null | undefined) =>
  parseRowsGeneric<PeakExemplarRow>(raw, EMPTY_PEAK_EXEMPLAR_ROW, "value", DEFAULT_ROW_COUNT);




const isRowEmpty = (row: Record<string, string>) =>
  Object.values(row).every((v) => !v || !v.trim());

function serializeRowsGeneric<R extends Record<string, string>>(rows: R[]): string {
  const cleaned = rows
    .map((r) => {
      const out = {} as Record<string, string>;
      for (const [k, v] of Object.entries(r)) out[k] = (v ?? "").trim();
      return out as R;
    })
    .filter((r) => !isRowEmpty(r));
  return cleaned.length === 0 ? "" : JSON.stringify(cleaned);
}

export const serializeSynthesisRows = (rows: SynthesisRow[]) => serializeRowsGeneric(rows);
export const serializeRhetoricalRows = (rows: RhetoricalMoveRow[]) => serializeRowsGeneric(rows);
export const serializeHighFrequencyTermRows = (rows: HighFrequencyTermRow[]) => serializeRowsGeneric(rows);
export const serializeSignaturePhraseRows = (rows: SignaturePhraseRow[]) => serializeRowsGeneric(rows);
export const serializeVerbalMarkerRows = (rows: VerbalMarkerRow[]) => serializeRowsGeneric(rows);
export const serializeSentenceArchitectureRows = (rows: SentenceArchitectureRow[]) => serializeRowsGeneric(rows);
export const serializeParagraphFlowRows = (rows: ParagraphFlowRow[]) => serializeRowsGeneric(rows);
export const serializeBuildingBlockProtocolRows = (rows: BuildingBlockProtocolRow[]) => serializeRowsGeneric(rows);
export const serializeRedLineRows = (rows: RedLineRow[]) => serializeRowsGeneric(rows);
export const serializeAvoidTermRows = (rows: AvoidTermRow[]) => serializeRowsGeneric(rows);
export const serializeMisrepresentationRows = (rows: MisrepresentationRow[]) => serializeRowsGeneric(rows);
export const serializeAuthenticityMarkerRows = (rows: AuthenticityMarkerRow[]) => serializeRowsGeneric(rows);
export const serializePeakExemplarRows = (rows: PeakExemplarRow[]) => serializeRowsGeneric(rows);

export type VoiceDraftState = {
  title: string;
  dual_voice_modes_enabled: boolean;
  // Free-form text
  phrase_preferences: string;
  avoid_list: AvoidTermRow[];
  // Aethyrius-parity extensions
  core_identity: string;
  lexical_compliance: string;
  voice_mode_a_label: string;
  voice_mode_b_label: string;
  voice_mode_a_notes: string;
  voice_mode_b_notes: string;
  sentence_architecture: SentenceArchitectureRow[];
  paragraph_flow: ParagraphFlowRow[];
  building_block_protocols: BuildingBlockProtocolRow[];
  // Structured row editors (Vocabulary fingerprint)
  vocabulary_fingerprint: HighFrequencyTermRow[];
  signature_phrases: SignaturePhraseRow[];
  verbal_markers: VerbalMarkerRow[];
  // Structured row editors (existing)
  rhetorical_moves: RhetoricalMoveRow[];
  tradition_synthesis: SynthesisRow[];
  // Remaining fields
  primary_tone: string;
  secondary_tone: string;
  formality_position: string;
  emotional_register: string;
  red_lines: RedLineRow[];
  common_misrepresentations: MisrepresentationRow[];
  peak_author_examples: PeakExemplarRow[];
  authenticity_markers: AuthenticityMarkerRow[];
};

type StringFieldKey = {
  [K in keyof VoiceDraftState]: VoiceDraftState[K] extends string ? K : never;
}[keyof VoiceDraftState];

export type VoiceProfileFieldKey = Exclude<
  StringFieldKey,
  "title" | "voice_mode_a_label" | "voice_mode_b_label"
>;

export type VoiceProfileSection = {
  title: string;
  description?: string;
  kind?:
    | "rhetorical-moves"
    | "synthesis"
    | "vocabulary-fingerprint"
    | "structural-patterns"
    | "boundaries"
    | "exemplars";
  fields: { key: VoiceProfileFieldKey; label: string; placeholder?: string; rows?: number }[];
};


export const CORE_IDENTITY_SECTION: VoiceProfileSection = {
  title: "1. Core Identity",
  description: "Who you are as a writer in one paragraph. Used by every downstream agent.",
  fields: [{ key: "core_identity", label: "Core Identity", rows: 4, placeholder: "Pastoral but rigorous. Synthesizing tradition with contemporary thought…" }],
};

export const LEXICON_SECTION: VoiceProfileSection = {
  title: "2. Lexicon Rules",
  description:
    "Locked terms, definitions, and hallucination guardrails the agents must respect verbatim.",
  fields: [
    {
      key: "lexical_compliance",
      label: "2.1 Lexical Compliance",
      rows: 6,
      placeholder:
        "List terms, names, spellings, or claims the AI must never invent, alter, or substitute. Include the exact form to use and any close-but-wrong variants to avoid.",
    },
  ],
};

export const VOICE_PROFILE_SECTIONS: VoiceProfileSection[] = [
  {
    title: "4. Structural Patterns",
    kind: "structural-patterns",
    fields: [],
  },
  {
    title: "5. Vocabulary Fingerprint",
    kind: "vocabulary-fingerprint",
    fields: [
      { key: "phrase_preferences", label: "5.4 Vocabulary Calibration", rows: 3 },
    ],
  },
  {
    title: "6. Rhetorical Signature Moves",
    description: "Named moves you reach for repeatedly. One row per move — add more as needed.",
    kind: "rhetorical-moves",
    fields: [],
  },
  {
    title: "7. Tone Markers",
    fields: [
      { key: "primary_tone", label: "7.1 Primary Tone", rows: 2 },
      { key: "secondary_tone", label: "7.2 Secondary Tone", rows: 2 },
      { key: "formality_position", label: "7.3 Formality Position", rows: 2 },
      { key: "emotional_register", label: "7.4 Emotional Register", rows: 2 },
    ],
  },
  {
    title: "8. Synthesis",
    description: "The ideas, thinkers, or traditions you build on or push back against — one row per referenced concept.",
    kind: "synthesis",
    fields: [],
  },
  {
    title: "9. Boundaries",
    kind: "boundaries",
    fields: [],
  },

  {
    title: "10. Exemplars",
    kind: "exemplars",
    fields: [],
  },
];
